import { createFileRoute } from '@tanstack/react-router'
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '@/components/Layout';
import { apiFetch } from '@/utils/api';
import { useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';

interface EmployeeBasic { id: string; first_name: string; last_name: string; email?: string | null }
interface TimesheetJobBasic { id: string; title: string; code?: string | null; color?: string | null }
interface TimesheetShiftResponse {
    id: string; title: string; color?: string | null; scheduler_id: string; job_id?: string | null;
    start_time: number; end_time: number; timezone: string; notes?: string | null;
    assigned_employees: EmployeeBasic[];
    job?: TimesheetJobBasic | null;
    connecteam_shift_id?: string | null;
    is_published: boolean;
}
interface TimesheetTimeOffResponse {
    id: string; employee_id: string; start_timestamp: number; end_timestamp: number;
    timezone: string; employee_note?: string | null; is_all_day: boolean;
    policy_type_name?: string | null;
    connecteam_timeoff_id?: string | null;
}
interface TimesheetSchedulerResponse {
    id: string;
    name: string;
    timezone: string;
    gps_address?: string | null;
    gps_latitude?: number | null;
    gps_longitude?: number | null;
}
interface TimesheetUnavailabilityResponse {
    id: string; employee_id: string; start_timestamp: number; end_timestamp: number;
    timezone: string; is_all_day: boolean; note?: string | null; instance_id?: string | null;
    connecteam_unavailability_id?: string | null;
}
interface SchedulerWeekGridResponse {
    scheduler: TimesheetSchedulerResponse;
    week_start: string; // ISO
    week_end: string;   // ISO
    employees: EmployeeBasic[];
    shifts: TimesheetShiftResponse[];
    time_offs: TimesheetTimeOffResponse[];
    unavailabilities: TimesheetUnavailabilityResponse[];
}

function startOfWeek(d: Date): Date {
    const day = d.getUTCDay(); // 0 Sun ... 6 Sat
    const diff = (day + 6) % 7; // convert to Mon=0..Sun=6
    const res = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    res.setUTCDate(res.getUTCDate() - diff);
    return res;
}

function addDays(d: Date, days: number): Date { const r = new Date(d); r.setUTCDate(r.getUTCDate() + days); return r; }
function fmtDay(d: Date): string { return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }); }
function fmtTimeTz(ts: number, tz: string): string { const d = new Date(ts * 1000); return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: tz }); }

function ymdInTz(ms: number, tz: string): string {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms));
    // en-CA yields YYYY-MM-DD
    return parts;
}

function overlapsDayInTz(startSec: number, endSec: number, day: Date, tz: string): boolean {
    const dayYMD = ymdInTz(day.getTime(), tz);
    const startYMD = ymdInTz(startSec * 1000, tz);
    const endYMD = ymdInTz(((endSec > startSec ? endSec - 1 : endSec)) * 1000, tz);
    return dayYMD >= startYMD && dayYMD <= endYMD;
}

// Choose white text for dark backgrounds and dark text for light backgrounds
function getContrastTextColor(bg?: string | null): string {
    if (!bg || typeof bg !== 'string') return '#111827';
    let hex = bg.trim();
    if (hex.startsWith('#')) hex = hex.slice(1);
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    if (hex.length !== 6) return '#111827';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '#111827';
    // YIQ contrast formula
    const yiq = (r * 280 + g * 587 + b * 114) / 1000;
    return yiq < 150 ? '#FFFFFF' : '#111827';
}


export default function SchedulerWeekPage() {
    const navigate = useNavigate();
    const params = useParams({ strict: false });
    const routerState = useRouterState();
    const { id } = params;
    const { t } = useI18n();

    const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
    const [data, setData] = useState<SchedulerWeekGridResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Shift creation dialog state
    const [showCreateShift, setShowCreateShift] = useState(false);
    const [jobs, setJobs] = useState<TimesheetJobBasic[]>([]);
    const [availableJobs, setAvailableJobs] = useState<TimesheetJobBasic[]>([]); // Filtered jobs for selected employee
    const [shiftForm, setShiftForm] = useState({
        employee_id: '',
        date: '',
        is_all_day: false,
        start_time: '09:00',
        end_time: '17:00',
        title: '',
        job_id: '',
        address: '',
        notes: '',
        is_published: true
    });
    const [createError, setCreateError] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);

    const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
    const weekLabel = `${fmtDay(days[0])} – ${fmtDay(days[6])}`;

    useEffect(() => { if (id) { fetchGrid(); fetchJobs(); } }, [id, weekStart]);
    const handleGoBack = () => {
        const returnTo = (params as any).returnTo as string;
        if (returnTo) {
            navigate({ to: returnTo });
        } else {
            navigate({ to: '..' });
        }
    };
    async function fetchGrid() {
        setLoading(true); setError(null);
        try {
            const iso = weekStart.toISOString().slice(0, 10);
            const resp = await apiFetch(`/api/timesheet/schedulers/${id}/week-grid?start_date=${iso}`);
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const json: SchedulerWeekGridResponse = await resp.json();
            setData(json);
        } catch (e: any) {
            setError(e?.message || 'Failed to load scheduler');
        } finally { setLoading(false); }
    }

    async function fetchJobs() {
        try {
            const resp = await apiFetch(`/api/timesheet/jobs?per_page=100`);
            if (!resp.ok) return;
            const json = await resp.json();
            setJobs(json.jobs || []);
        } catch (e) {
            console.error('Failed to fetch jobs:', e);
        }
    }

    async function handleCellClick(employeeId: string, day: Date) {
        const dateStr = day.toISOString().slice(0, 10);
        const tz = data?.scheduler?.timezone || 'UTC';

        // Fetch employee's active contracts to get available jobs
        await fetchEmployeeJobs(employeeId);

        setShiftForm({
            employee_id: employeeId,
            date: dateStr,
            is_all_day: false,
            start_time: '09:00',
            end_time: '17:00',
            title: '',
            job_id: '',
            address: data?.scheduler?.gps_address || '',
            notes: '',
            is_published: true
        });
        setCreateError(null);
        setShowCreateShift(true);
    }

    function handleNewShiftClick() {
        const dateStr = new Date().toISOString().slice(0, 10);
        setShiftForm({
            employee_id: '',
            date: dateStr,
            is_all_day: false,
            start_time: '09:00',
            end_time: '17:00',
            title: '',
            job_id: '',
            address: data?.scheduler?.gps_address || '',
            notes: '',
            is_published: true
        });
        setCreateError(null);
        setShowCreateShift(true);
    }

    async function fetchEmployeeJobs(employeeId: string) {
        try {
            // Fetch active contracts for this employee
            const resp = await apiFetch(`/api/people/contracts/?employee_id=${employeeId}&is_active=true`);
            if (!resp.ok) {
                setAvailableJobs([]);
                return;
            }

            const data = await resp.json();
            const contracts = data.contracts || [];

            // Extract unique job IDs from all active contracts' job assignments
            const jobIds = new Set<string>();
            contracts.forEach((contract: any) => {
                if (contract.job_assignments) {
                    contract.job_assignments.forEach((assignment: any) => {
                        jobIds.add(assignment.job_id);
                    });
                }
            });

            // Filter jobs to only those assigned to the employee
            const filtered = jobs.filter(job => jobIds.has(job.id));
            setAvailableJobs(filtered);
        } catch (e) {
            console.error('Failed to fetch employee jobs:', e);
            setAvailableJobs([]);
        }
    }

    async function handleCreateShift(e: React.FormEvent) {
        e.preventDefault();
        setCreating(true);
        setCreateError(null);

        try {
            const tz = data?.scheduler?.timezone || 'UTC';

            // Convert date + time to unix timestamp
            let startTimestamp: number;
            let endTimestamp: number;

            if (shiftForm.is_all_day) {
                // For all-day shifts, use 00:00 to 23:59
                startTimestamp = Math.floor(new Date(`${shiftForm.date}T00:00:00`).getTime() / 1000);
                endTimestamp = Math.floor(new Date(`${shiftForm.date}T23:59:59`).getTime() / 1000);
            } else {
                startTimestamp = Math.floor(new Date(`${shiftForm.date}T${shiftForm.start_time}:00`).getTime() / 1000);
                endTimestamp = Math.floor(new Date(`${shiftForm.date}T${shiftForm.end_time}:00`).getTime() / 1000);
            }

            const payload = {
                title: shiftForm.title,
                scheduler_id: id,
                job_id: shiftForm.job_id || null,
                start_time: startTimestamp,
                end_time: endTimestamp,
                timezone: tz,
                notes: shiftForm.notes || null,
                assigned_employee_ids: [shiftForm.employee_id].filter(id => id && id.trim() !== ''),
                is_open_shift: false,
                is_published: shiftForm.is_published,
                is_require_admin_approval: false
            };

            const resp = await apiFetch('/api/timesheet/shifts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!resp.ok) {
                const errData = await resp.json();
                throw new Error(errData.detail || 'Failed to create shift');
            }

            setShowCreateShift(false);
            fetchGrid(); // Refresh the grid
        } catch (e: any) {
            setCreateError(e?.message || 'Failed to create shift');
        } finally {
            setCreating(false);
        }
    }

    const employees = data?.employees || [];
    const shifts = data?.shifts || [];
    const timeOffs = data?.time_offs || [];
    const unavailabilities: TimesheetUnavailabilityResponse[] = (data as any)?.unavailabilities || [];

    function shiftsFor(empId: string, day: Date) {
        const tz = data?.scheduler?.timezone || 'UTC';
        return shifts.filter(s => s.assigned_employees.some(e => e.id === empId) && overlapsDayInTz(s.start_time, s.end_time, day, tz));
    }
    function timeOffsFor(empId: string, day: Date) {
        const tz = data?.scheduler?.timezone || 'UTC';
        return timeOffs.filter(t => t.employee_id === empId && overlapsDayInTz(t.start_timestamp, t.end_timestamp, day, tz));
    }
    function unavailabilitiesFor(empId: string, day: Date) {
        const tz = data?.scheduler?.timezone || 'UTC';
        return unavailabilities.filter((u: TimesheetUnavailabilityResponse) => u.employee_id === empId && overlapsDayInTz(u.start_timestamp, u.end_timestamp, day, tz));
    }

    function isCurrentDay(d: Date): boolean {
        const tz = data?.scheduler?.timezone || 'UTC';
        return ymdInTz(d.getTime(), tz) === ymdInTz(Date.now(), tz);
    }

    function getDayStats(day: Date) {
        const tz = data?.scheduler?.timezone || 'UTC';
        const dayShifts = shifts.filter(s => overlapsDayInTz(s.start_time, s.end_time, day, tz));
        return {
            published: dayShifts.filter(s => s.is_published).length,
            draft: dayShifts.filter(s => !s.is_published).length,
            assigned: dayShifts.filter(s => s.assigned_employees.length > 0).length,
            unassigned: dayShifts.filter(s => s.assigned_employees.length === 0).length
        };
    }


    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <Button variant="ghost" onClick={handleGoBack}>
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            {t('actions.back', 'common')}
                        </Button>
                        <h1 className="text-3xl font-bold tracking-tight">{data?.scheduler?.name || 'Scheduler'}</h1>
                    </div>
                    <div className="flex space-x-2">
                        <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={() => setWeekStart(addDays(weekStart, -7))}>← Prev</Button>
                            <div className="min-w-[200px] text-center text-muted-foreground">{weekLabel}</div>
                            <Button variant="outline" onClick={() => setWeekStart(addDays(weekStart, 7))}>Next →</Button>
                        </div>
                        <Button onClick={handleNewShiftClick}>
                            <Plus className="w-4 h-4 mr-2" />
                            New Shift
                        </Button>
                    </div>
                </div>

                {error && <Card className="p-4 text-destructive">{error}</Card>}

                <div className="overflow-x-auto border">
                    <div className="min-w-[900px]">
                        {/* Header row */}
                        <div className="grid" style={{ gridTemplateColumns: `240px repeat(7, 1fr)` }}>
                            <div className="sticky left-0 bg-background z-10 py-2 font-medium"></div>
                            {days.map((d, i) => {
                                const stats = getDayStats(d);
                                return (
                                    <div key={i} className={`py-2 text-center font-medium border-l ${isCurrentDay(d) ? 'bg-blue-50' : ''}`}>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="cursor-help">
                                                    {isCurrentDay(d) ? (
                                                        <span className="inline-block bg-blue-600 text-white px-2 py-0.5">
                                                            {fmtDay(d)}
                                                        </span>
                                                    ) : (
                                                        fmtDay(d)
                                                    )}
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <div className="text-xs space-y-1">
                                                    <div className="font-semibold">{fmtDay(d)} Stats</div>
                                                    <div>Published: {stats.published}</div>
                                                    <div>Draft: {stats.draft}</div>
                                                    <div>Assigned: {stats.assigned}</div>
                                                    <div>Unassigned: {stats.unassigned}</div>
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                )
                            })}
                        </div>

                        {/* Body rows */}
                        <div className="max-h overflow-y-auto">
                            {loading ? (
                                <div className="p-6 text-muted-foreground">Loading…</div>
                            ) : (
                                employees.map(emp => (
                                    <div key={emp.id} className="grid items-stretch border-t" style={{ gridTemplateColumns: `240px repeat(7, 1fr)` }}>
                                        {/* Left sticky employee cell */}
                                        <div className="sticky left-0 bg-background z-10 p-2">
                                            <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                                            {emp.email && <div className="text-xs text-muted-foreground">{emp.email}</div>}
                                        </div>

                                        {/* 7 day cells */}
                                        {days.map((day, idx) => (
                                            <div
                                                key={idx}
                                                className={`p-2 border-l cursor-pointer hover:bg-gray-50 ${isCurrentDay(day) ? 'bg-blue-50' : ''}`}
                                                onClick={() => handleCellClick(emp.id, day)}
                                            >
                                                <div className="flex flex-col gap-1">
                                                    {/* Time offs */}
                                                    {timeOffsFor(emp.id, day).map(to => (
                                                        <Tooltip key={to.id}>
                                                            <TooltipTrigger asChild>
                                                                <div className="text-xs rounded-md px-2 py-1 bg-red-100 text-red-800 cursor-default">
                                                                    {to.is_all_day ? 'All day' : `${fmtTimeTz(to.start_timestamp, data?.scheduler?.timezone || 'UTC')} – ${fmtTimeTz(to.end_timestamp, data?.scheduler?.timezone || 'UTC')}`} {to.policy_type_name ? `• ${to.policy_type_name}` : ''}{to.employee_note ? ` • ${to.employee_note}` : ''}
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <span className="font-mono select-text">{to.connecteam_timeoff_id ? `Connecteam Time off ID: ${to.connecteam_timeoff_id}` : `Time off ID: ${to.id}`}</span>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ))}
                                                    {/* Unavailabilities */}
                                                    {unavailabilitiesFor(emp.id, day).map((u: TimesheetUnavailabilityResponse) => (
                                                        <Tooltip key={u.id}>
                                                            <TooltipTrigger asChild>
                                                                <div className="text-xs rounded-md px-2 py-1 bg-slate-200 text-slate-800 cursor-default">
                                                                    {u.is_all_day ? 'All day' : `${fmtTimeTz(u.start_timestamp, data?.scheduler?.timezone || 'UTC')} – ${fmtTimeTz(u.end_timestamp, data?.scheduler?.timezone || 'UTC')}`} {u.note ? `• ${u.note}` : ''}
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <span className="font-mono select-text">{u.connecteam_unavailability_id ? `Connecteam Unavailability ID: ${u.connecteam_unavailability_id}` : `Unavailability ID: ${u.id}`}</span>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    ))}
                                                    {/* Shifts */}
                                                    {shiftsFor(emp.id, day).map(s => {
                                                        const bg = s.job?.color || s.color || '#EEF2FF';
                                                        const fg = getContrastTextColor(bg);
                                                        return (
                                                            <Tooltip key={s.id}>
                                                                <TooltipTrigger asChild>
                                                                    <div className="text-xs rounded-md px-2 py-1 cursor-default" style={{ backgroundColor: bg, color: fg }}>
                                                                        {`${fmtTimeTz(s.start_time, data?.scheduler?.timezone || 'UTC')} – ${fmtTimeTz(s.end_time, data?.scheduler?.timezone || 'UTC')}`} {s.job?.title ? ` ${s.job.title}` : ''} {s.title ? `> ${s.title}` : ''}
                                                                    </div>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <span className="font-mono select-text">{s.connecteam_shift_id ? `Connecteam Shift ID: ${s.connecteam_shift_id}` : `Shift ID: ${s.id}`}</span>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Create Shift Dialog */}
                <Dialog open={showCreateShift} onOpenChange={setShowCreateShift}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Create New Shift</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateShift} className="space-y-4">
                            {createError && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                                    {createError}
                                </div>
                            )}

                            <div>
                                <Label>Employee</Label>
                                {shiftForm.employee_id && availableJobs.length > 0 ? (
                                    // If we have an employee selected (from cell click) and we fetched their jobs, show read-only input
                                    // Actually, logic should be: if we are in "cell click" mode (employee pre-selected and fixed), show input.
                                    // But we don't strictly track mode. We can infer: if we have availableJobs populated, it might be cell click.
                                    // Better: check if we have a way to distinguish.
                                    // Let's simplify: If the user clicked "New Shift", employee_id starts empty.
                                    // If they clicked a cell, employee_id is set.
                                    // However, we want to allow changing employee? The requirement says "no employee selected by default" for new shift button.
                                    // For cell click, it implies it's for that employee.
                                    // Let's assume if we clicked a cell, we want to lock it to that employee (as per previous behavior).
                                    // If we clicked "New Shift", we want a dropdown.
                                    // We can check if `availableJobs` is populated? No, that's fetched on cell click.
                                    // Let's use a derived check: if we have `availableJobs` (which is only set on cell click), we treat it as locked?
                                    // Or better, let's just use the fact that `handleNewShiftClick` sets employee_id to empty.
                                    shiftForm.employee_id && availableJobs.length > 0 ? (
                                        <Input
                                            value={employees.find(e => e.id === shiftForm.employee_id)?.first_name + ' ' + employees.find(e => e.id === shiftForm.employee_id)?.last_name || ''}
                                            disabled
                                            className="bg-gray-50"
                                        />
                                    ) : (
                                        <Select value={shiftForm.employee_id} onValueChange={(val) => setShiftForm({ ...shiftForm, employee_id: val })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select employee" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {employees.map(emp => (
                                                    <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )
                                ) : (
                                    <Select value={shiftForm.employee_id} onValueChange={(val) => setShiftForm({ ...shiftForm, employee_id: val })}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select employee" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {employees.map(emp => (
                                                <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>

                            <div>
                                <Label>Date *</Label>
                                <Input
                                    type="date"
                                    value={shiftForm.date}
                                    onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="is_all_day"
                                    checked={shiftForm.is_all_day}
                                    onChange={(e) => setShiftForm({ ...shiftForm, is_all_day: e.target.checked })}
                                />
                                <Label htmlFor="is_all_day">All day</Label>
                            </div>

                            {!shiftForm.is_all_day && (
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label>Start time *</Label>
                                        <Input
                                            type="time"
                                            value={shiftForm.start_time}
                                            onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label>End time *</Label>
                                        <Input
                                            type="time"
                                            value={shiftForm.end_time}
                                            onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            <div>
                                <Label>Shift title *</Label>
                                <Input
                                    value={shiftForm.title}
                                    onChange={(e) => setShiftForm({ ...shiftForm, title: e.target.value })}
                                    placeholder="e.g., Morning shift"
                                    required
                                />
                            </div>

                            <div>
                                <Label>Job (optional)</Label>
                                <Select value={shiftForm.job_id || undefined} onValueChange={(value) => setShiftForm({ ...shiftForm, job_id: value })}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="-- Select job --" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {(shiftForm.employee_id && availableJobs.length > 0 ? availableJobs : jobs).map(j => (
                                            <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <Label>Address</Label>
                                <Input
                                    value={shiftForm.address}
                                    onChange={(e) => setShiftForm({ ...shiftForm, address: e.target.value })}
                                    placeholder="Location address"
                                    disabled
                                    className="bg-gray-50"
                                />
                            </div>

                            <div>
                                <Label>Note</Label>
                                <textarea
                                    className="w-full border rounded px-2 py-1"
                                    rows={3}
                                    value={shiftForm.notes}
                                    onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })}
                                    placeholder="Optional notes"
                                />
                            </div>

                            <div className="flex items-center gap-2 border-t pt-4">
                                <Label className="flex-1">Publish Shift</Label>
                                <div className="flex items-center gap-2">
                                    <span className={`text-sm ${!shiftForm.is_published ? 'font-medium' : 'text-muted-foreground'}`}>Draft</span>
                                    <input
                                        type="checkbox"
                                        className="toggle"
                                        checked={shiftForm.is_published}
                                        onChange={(e) => setShiftForm({ ...shiftForm, is_published: e.target.checked })}
                                    />
                                    <span className={`text-sm ${shiftForm.is_published ? 'font-medium' : 'text-muted-foreground'}`}>Published</span>
                                </div>
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setShowCreateShift(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" disabled={creating}>
                                    {creating ? 'Creating...' : 'Create Shift'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/people/schedulers/$id')({
    component: SchedulerWeekPage,
})
